// PaywallCard — branded "this is a Pro+ feature" card. Replaces a paid
// surface's main content when useUserTier().hasTier(requiredTier) is false.
//
// Behaviour:
//   - Anon: CTA routes to /auth?intent=signup&plan=<plan_code> (or /pricing
//     if no plan is seeded yet).
//   - Authenticated: CTA invokes the paystack-checkout Edge fn and
//     redirects to authorization_url.
//   - 503 payments_not_configured: shows the polite "launching soon" line.
//
// Doctrine: never bypass-gate "for testing". Never fabricate price.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { initiatePaystackCheckout } from "../billing/initiatePaystackCheckout.ts";
import { formatPlanPrice, usePlanForTier } from "../billing/usePlanForTier.ts";
import type { TierGate } from "../auth/tier-gates.ts";

export interface PaywallCardProps {
  requiredTier: TierGate;
  featureName: string;
  pitch: string;
  /** T3.3 — optional public trust stat (e.g. the trailing-30d
   *  positive-CLV headline). Verifiable, never fabricated; rendered
   *  as a small evidence line under the pitch. */
  trustStat?: string | null;
}

const TIER_LABELS: Record<TierGate, string> = {
  free: "FREE",
  sika_pro: "SIKA PRO",
  phantom_pro: "PRO",
  desk_pro: "ELITE",
};

export function PaywallCard({
  requiredTier,
  featureName,
  pitch,
  trustStat,
}: PaywallCardProps): JSX.Element {
  const { user } = useAuthSession();
  const navigate = useNavigate();
  const { plan, loading: planLoading } = usePlanForTier(requiredTier);
  const [busy, setBusy] = useState<boolean>(false);
  const [notice, setNotice] = useState<string | null>(null);

  const tierLabel = TIER_LABELS[requiredTier];

  async function handleSubscribe(): Promise<void> {
    setNotice(null);
    if (!plan) {
      // No plan seeded — push to /pricing so the user can pick a tier.
      navigate("/pricing");
      return;
    }
    if (!user) {
      navigate(`/auth?intent=signup&plan=${encodeURIComponent(plan.plan_code)}`);
      return;
    }
    setBusy(true);
    const result = await initiatePaystackCheckout(plan.plan_code);
    setBusy(false);
    switch (result.kind) {
      case "redirect":
        window.location.href = result.authorization_url;
        return;
      case "needs_auth":
        navigate(`/auth?intent=signup&plan=${encodeURIComponent(plan.plan_code)}`);
        return;
      case "not_configured":
        setNotice(result.message);
        return;
      case "error":
        setNotice(result.message);
        return;
    }
  }

  return (
    <div
      className="card paywall-card"
      style={{
        background: "var(--surface, #0e0e10)",
        border: "1px solid var(--border, #2a2a30)",
        padding: "32px 28px",
        borderRadius: 12,
        color: "var(--text, #fff)",
        maxWidth: 520,
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 11,
          letterSpacing: "0.18em",
          color: "var(--accent, #b07cff)",
          textTransform: "uppercase",
          marginBottom: 12,
        }}
      >
        STRIKZA · {tierLabel} feature
      </div>
      <h2 style={{ fontSize: 22, lineHeight: 1.25, margin: "0 0 10px" }}>{featureName}</h2>
      <p style={{ fontSize: 14, lineHeight: 1.55, color: "var(--text-muted, #c8cad0)", margin: "0 0 22px" }}>
        {pitch}
      </p>
      {trustStat && (
        <a
          href="/intelligence-record"
          data-testid="paywall-trust-stat"
          style={{
            display: "block",
            fontSize: 12.5,
            lineHeight: 1.5,
            color: "var(--text-muted, #c8cad0)",
            borderLeft: "2px solid var(--sa-accent-saffron, #e0a106)",
            paddingLeft: 10,
            margin: "0 0 22px",
            textDecoration: "none",
          }}
        >
          {trustStat} <span style={{ opacity: 0.7 }}>· verify the record →</span>
        </a>
      )}

      {planLoading ? (
        <div className="mono" style={{ fontSize: 12, color: "var(--text-tertiary, #888)" }}>
          Loading plan…
        </div>
      ) : plan ? (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            paddingBottom: 18,
            borderBottom: "1px solid var(--border, #2a2a30)",
            marginBottom: 18,
          }}
        >
          <span style={{ fontSize: 28, fontWeight: 600 }}>{formatPlanPrice(plan)}</span>
          <span
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.14em",
              color: "var(--text-muted, #c8cad0)",
              textTransform: "uppercase",
              marginLeft: 6,
            }}
          >
            / {plan.interval}
          </span>
        </div>
      ) : (
        <div
          className="mono"
          style={{ fontSize: 11, color: "var(--text-tertiary, #888)", marginBottom: 18 }}
        >
          Subscription tiers launching soon.
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button
          type="button"
          onClick={() => {
            void handleSubscribe();
          }}
          disabled={busy || planLoading}
          style={{
            padding: "10px 16px",
            borderRadius: 10,
            background: "var(--accent, #b07cff)",
            color: "#0a0a0a",
            fontWeight: 700,
            fontSize: 13,
            border: "none",
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? "Redirecting…" : plan ? "Subscribe via Paystack" : "See pricing"}
        </button>
        <Link
          to="/pricing"
          style={{
            fontSize: 12,
            color: "var(--text-muted, #c8cad0)",
            textDecoration: "none",
          }}
        >
          Compare tiers →
        </Link>
      </div>

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 16,
            padding: "10px 12px",
            background: "rgba(176, 124, 255, 0.08)",
            border: "1px solid var(--border, #2a2a30)",
            borderRadius: 8,
            fontSize: 12,
            color: "var(--text-muted, #c8cad0)",
          }}
        >
          {notice}
        </div>
      ) : null}
    </div>
  );
}
